import { StickyBottomBar } from '@/components/common/StickyBottomBar';
import { AccountPageHeader as PageHeader, AccountFormShell } from '@/components/common/organism/AccountPageLayout';
import { useListHeaderHeight } from '@/components/common/organism/ListPageLayout';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { STEP_META, SeminarFormStep, SeminarStepMeta } from '@/pages/Seminar/components/form/seminarFormModel';
import { claudeTokens } from '@/styles/claudeTokens';
import { designSystem } from '@/styles/theme';
import styled from '@emotion/styled';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import React from 'react';
import { useHeaderBackAction } from '@/common/headerBack';

/**
 * 세미나 등록·수정 폼 틀 — 폰(≤768) 5단계 / PC(>768) 2칸(가운데 단계 카드 · 오른쪽 미리보기+단계 목록+등록).
 * 폰에서도 모든 단계 카드를 그려 두고 지금 단계만 보인다(입력 부품의 내부 상태가 단계 이동에 날아가지 않게).
 * 고정 줄은 모두 sticky(앱 웹뷰에서 fixed 는 당겨서 새로고침·스와이프 뒤로가기 transform 과 함께 튄다).
 */

export const PHONE_QUERY: string = '(max-width:768px)';
const PHONE: string = '@media (max-width: 768px)';
const PC: string = '@media (min-width: 769px)';

/** 앱 공용 헤더(sticky) 높이 — 폼의 sticky 줄을 그 아래에 붙인다 */
export const useHeaderHeight = (): number => useListHeaderHeight(true);

/** 제목·단계/탭·입력을 하나의 흰 셸에 둔다. 폭과 여백은 계정 화면과 같은 기준이다. */
export const FormPage = styled(AccountFormShell)`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 70vh;
  padding-bottom: 0;
  color: ${FORM_COLOR.text};
`;

// ─── 맨 위 줄 ─────────────────────────────────────────────────────────

const SaveState = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${FORM_COLOR.muted};
  white-space: nowrap;
`;

/** 모바일도 공용 22px 제목을 사용하고 뒤로가기는 앱 헤더가 담당한다. 임시저장은 제목 오른쪽에 유지한다. */
export const FormTopBar: React.FC<{ title: string; savedText: string; onExit: () => void; onSave?: () => void; saving?: boolean }> = ({ title, savedText, onExit, onSave, saving }) => {
  useHeaderBackAction(onExit);
  return (
  <PageHeader
    title={title}
    actions={onSave || savedText ? <SaveState aria-live="polite">{onSave ? <button type="button" onClick={onSave} disabled={saving} style={{ border: 0, background: 'none', color: FORM_COLOR.primaryDeep, font: 'inherit', padding: '12px 0' }}>임시저장</button> : savedText}</SaveState> : undefined}
  />
  );
};

// ─── 폰: 진행 막대 ──────────────────────────────────────────────────────

const ProgressRoot = styled.div<{ top: number }>`
  position: sticky;
  top: ${({ top }) => top}px;
  z-index: ${designSystem.zIndex.sticky};
  margin: 0 -8px;
  padding: 6px 8px 12px;
  background: ${claudeTokens.color.surface};
  border-bottom: 1px solid ${FORM_COLOR.line};
`;

const Bars = styled.div`
  display: grid;
  /* 평소엔 다섯 칸 같은 폭, 좁은 폰(320)에서 긴 이름("모집·참가비")은 그 칸만 글자만큼 넓어진다 */
  grid-template-columns: repeat(5, minmax(max-content, 1fr));
  gap: 6px;
`;

/** 칸 하나 = 막대 + 그 단계 이름(B안, 2026-10-06 사용자 결정 — 이력서처럼 단계 이름을 다 보여 준다) */
const BarButton = styled('button', { shouldForwardProp: (prop: string) => prop !== 'filled' && prop !== 'current' })<{ filled: boolean; current: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 8px 0 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:disabled {
    cursor: default;
  }

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: ${({ filled }) => (filled ? FORM_COLOR.primary : FORM_COLOR.line)};
  }

  > span {
    display: block;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ filled, current }) => (current ? FORM_COLOR.primaryDeep : filled ? FORM_COLOR.sub : FORM_COLOR.muted)};
    font-weight: ${({ current }) => (current ? 700 : 400)};
  }
`;

/** 5칸 진행 막대 — 칸마다 단계 이름(지금 단계 굵게). 칸을 누르면 그 단계로(canSelect 가 허락한 단계만) */
export const ProgressStrip: React.FC<{
  step: SeminarFormStep;
  top: number;
  /** 수정 화면 — 마지막 단계 이름을 "확인하고 저장"으로(읽기 전용 안내용 aria) */
  editMode?: boolean;
  steps?: SeminarStepMeta[];
  canSelect: (step: SeminarFormStep) => boolean;
  onSelect: (step: SeminarFormStep) => void;
}> = ({ step, top, editMode = false, steps = STEP_META, canSelect, onSelect }) => (
  <ProgressRoot top={top}>
    <Bars role="list" aria-label="진행 단계">
      {steps.map((item) => {
        const name: string = item.bar ?? item.progress;
        const fullName: string = editMode && item.step === 5 ? '확인하고 저장' : item.progress;
        return (
          <BarButton
            key={item.step}
            type="button"
            role="listitem"
            filled={item.step <= step}
            current={item.step === step}
            disabled={!canSelect(item.step) || item.step === step}
            aria-current={item.step === step ? 'step' : undefined}
            aria-label={`${item.step}단계 ${fullName}`}
            onClick={() => onSelect(item.step)}
          >
            <span>{name}</span>
          </BarButton>
        );
      })}
    </Bars>
  </ProgressRoot>
);

// ─── 단계 카드 ────────────────────────────────────────────────────────

const CardRoot = styled.section<{ active: boolean; dim: boolean }>`
  min-width: 0;
  word-break: keep-all;
  text-wrap: pretty;
  ${PHONE} {
    display: ${({ active }) => (active ? 'block' : 'none')};
    margin: 0;
    padding: 24px 0;
    background: ${claudeTokens.color.surface};
  }

  ${PC} {
    margin-bottom: 0;
    border-bottom: 1px solid ${FORM_COLOR.line};
    background: ${claudeTokens.color.surface};
    opacity: ${({ dim }) => (dim ? 0.55 : 1)};
    padding: ${({ active }) => (active ? '24px 0' : '0')};
    &:first-of-type { padding-top: 0; }
    &:last-of-type { border-bottom: none; }
  }
`;

/** 페이지 제목과 구분되는 공용 섹션 제목. */
export const CardHead = styled.div`
  h2 {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    color: ${FORM_COLOR.text};
  }

  p {
    margin: 0 0 22px;
    font-size: 14px;
    line-height: 1.5;
    color: ${FORM_COLOR.sub};
    word-break: keep-all;
  }

  ${PC} {
    h2 {
      font-size: 18px;
      margin-bottom: 4px;
    }

    p {
      font-size: 13.5px;
      color: ${FORM_COLOR.muted};
      margin-bottom: 18px;
    }
  }
`;

/** PC 접힌 카드 머리 — 누르면 그 단계를 연다 */
const CollapsedHead = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 20px 0;
  border: none;
  border-radius: ${claudeTokens.radius.md};
  background: none;
  text-align: left;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  strong {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 18px;
    font-weight: 800;
    color: ${FORM_COLOR.sub};

    svg {
      font-size: 20px;
      color: ${FORM_COLOR.primary};
    }
  }

  span {
    font-size: 13.5px;
    color: ${FORM_COLOR.muted};
  }
`;

/** 접힌 카드 안 입력은 그대로 두되(상태 보존) 보이지 않게 */
const CardBody = styled('div', { shouldForwardProp: (prop: string) => prop !== 'isOpen' })<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const CardFooter = styled.div`
  display: none;

  ${PC} {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }
`;

export const StepCard: React.FC<{
  step: SeminarFormStep;
  active: boolean;
  isPhone: boolean;
  /** PC 접힌 머리의 체크 표시 */
  done: boolean;
  /** PC 에서 아직 갈 수 없는(지나오지 않은) 단계 — 흐리게 */
  locked: boolean;
  sub: string;
  /** 카드 제목을 바꿀 때(수정 화면 확인 단계) */
  titleOverride?: string;
  steps?: SeminarStepMeta[];
  onOpen: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, active, isPhone, done, locked, sub, titleOverride, steps = STEP_META, onOpen, footer, children }) => {
  const meta: SeminarStepMeta = steps[step - 1];
  const cardTitle: string = titleOverride ?? meta.cardTitle;
  const open: boolean = isPhone ? true : active;
  return (
    <CardRoot id={`seminar-step-${step}`} active={active} dim={!isPhone && !active && locked} aria-label={cardTitle}>
      {!isPhone && !active ? (
        <CollapsedHead type="button" onClick={onOpen} disabled={locked} aria-expanded={false}>
          <strong>
            {cardTitle}
            {done && <CheckCircleOutlineIcon aria-label="다 채움" />}
          </strong>
          {!done && <span>{sub}</span>}
        </CollapsedHead>
      ) : (
        <CardHead>
          <h2>{isPhone ? meta.question : cardTitle}</h2>
          <p>{sub}</p>
        </CardHead>
      )}
      <CardBody isOpen={open}>
        {children}
        {footer && <CardFooter>{footer}</CardFooter>}
      </CardBody>
    </CardRoot>
  );
};

// ─── PC: 2칸(가운데 폼 · 오른쪽 미리보기+단계 목록+등록) ─────────────────────────

/**
 * 바깥 FormPage가 공용 폭과 좌우 여백을 관리한다. 헤더·입력·미리보기는 그 안에서 추가 보정 없이 정렬한다.
 */
const PcWidth = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const PcGrid = styled(PcWidth)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  padding-bottom: 48px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 20px;
    padding-bottom: 40px;
  }
`;

const PcSaveState = styled.span`
  padding-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${FORM_COLOR.muted};
  white-space: nowrap;
`;

/**
 * PC 머리줄 — 공용 PageHeader(24px 제목 + 오른쪽 actions)를 흰 셸 안에 둔다.
 * 왼쪽 끝은 폼 카드, 오른쪽 끝(임시 저장 상태)은 오른쪽 칸과 맞는다. 앱 헤더 뒤로가기는 기존 나가기 확인을 거친다.
 */
export const PcFormHeader: React.FC<{ title: string; savedText: string; onExit: () => void }> = ({ title, savedText, onExit }) => {
  useHeaderBackAction(onExit);
  return (
  <PcWidth>
    <PageHeader
      title={title}
      actions={savedText ? <PcSaveState aria-live="polite">{savedText}</PcSaveState> : undefined}
    />
  </PcWidth>
  );
};

/** 오른쪽 고정 칸 — 화면보다 길면(낮은 창) 칸 안에서 스크롤해 [등록] 버튼까지 닿게 한다 */
export const PcSide = styled.aside<{ top: number }>`
  position: sticky;
  top: ${({ top }) => top + 20}px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: calc(100vh - ${({ top }) => top + 40}px);
  overflow-y: auto;
  scrollbar-width: thin;

  > * {
    flex-shrink: 0;
  }
`;

// ─── 버튼 ─────────────────────────────────────────────────────────────

export const PrimaryButton = styled.button<{ look?: 'ready' | 'waiting' }>`
  flex: 1;
  height: 52px;
  padding: 0 16px;
  border: none;
  border-radius: 14px;
  background: ${({ look }) => (look === 'waiting' ? FORM_COLOR.primaryDisabled : FORM_COLOR.primary)};
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

export const GhostButton = styled.button`
  flex: 0 0 96px;
  height: 52px;
  border: 1px solid ${FORM_COLOR.border};
  border-radius: 14px;
  background: #fff;
  color: ${FORM_COLOR.sub};
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
`;

/** 폰 아래 고정 줄 — [이전] [다음/등록]. 페이지 전체 틀의 마지막 자식이라 끝까지 붙어 있다 */
export const PhoneActionBar = styled(StickyBottomBar)`
  margin: 0;
  gap: 10px;
  padding: 12px 0;
  background: ${claudeTokens.color.surface};
  border-top: 1px solid ${FORM_COLOR.line};

  ${PHONE} {
    margin: 0 -8px;
    padding: 12px 8px;

    /* 글 입력 중(화면 키보드)에는 숨긴다 — 키보드 바로 위로 올라와 입력칸을 가렸다(2026-10-06 안드로이드 실측).
       키보드 높이를 더해 올리지 않는다(두 번 더하기 사고). 신호는 Header 의 body[data-input-focused](하단 탭과 같다 — 포커스 + 키보드가 실제로 떠 있을 때, common/inputKeyboard.ts).
       자리는 두고 안 보이게만 — 빼면 문서가 줄 높이만큼 짧아져 페이지 끝 입력칸이 아래로 튄다(2026-10-06 측정 97px) */
    body[data-input-focused='true'] & {
      visibility: hidden;
      pointer-events: none;
    }
  }
`;

/*
 * 긴 폼 저장 바(FormSaveBar)·지원서 처리 줄 안의 버튼 — 공고 폼 하단 버튼과 같은 모양(52px·반경 14·800).
 * PC 는 오른쪽에 160px 이상으로 모으고, 폰은 줄을 나눠 꽉 채운다.
 * 저장 바의 `> *` 배치 규칙과 부품 기본 flex 가 같은 우선순위라 그리는 순서에 따라 뒤집히지 않게 && 로 올려 둔다.
 */
const SAVE_BAR_BUTTON_LAYOUT = `
  && {
    box-sizing: border-box;
    flex: 0 0 auto;
    min-width: 160px;
    padding: 0 24px;
  }

  ${PHONE} {
    && {
      /* 0 이 아니라 50% 에서 줄여 나눈다 — 테두리 있는 보조 버튼과 없는 주 버튼이 같은 폭이 되게(0 이면 테두리 2px 만큼 갈린다) */
      flex: 1 1 50%;
      min-width: 0;
      padding: 0 16px;
    }
  }
`;

/** 저장 바 주 버튼(저장·작성 완료·합격 등) */
export const SaveBarPrimaryButton = styled(PrimaryButton)`
  ${SAVE_BAR_BUTTON_LAYOUT}
`;

/** 저장 바 보조 버튼(불합격·목록으로 등) — 흰 바탕 테두리 */
export const SaveBarGhostButton = styled(GhostButton)`
  ${SAVE_BAR_BUTTON_LAYOUT}
`;

// ─── 이어 쓰기 안내 ──────────────────────────────────────────────────────

const ResumeRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 16px 0 0;
  padding: 14px 16px;
  border: 1px solid ${FORM_COLOR.primary};
  border-radius: 14px;
  background: ${FORM_COLOR.primaryCard};

  strong {
    font-size: 15px;
    font-weight: 800;
    color: ${FORM_COLOR.primaryDeep};
  }

  span {
    font-size: 13px;
    color: ${FORM_COLOR.sub};
  }

  div {
    display: flex;
    gap: 8px;
  }

  button {
    flex: 1;
    height: 44px;
    border-radius: 12px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 800;
    cursor: pointer;
  }

  ${PC} {
    margin: 0 0 18px;
  }
`;

/** "작성하던 세미나가 있어요 [이어서 쓰기][새로 쓰기]" — 대화 상자가 아니라 본문 위 상자 */
export const ResumeDraftBanner: React.FC<{ title: string; savedAt: number; entityLabel?: string; onResume: () => void; onFresh: () => void }> = ({ title, savedAt, entityLabel = '세미나', onResume, onFresh }) => {
  const saved: Date = new Date(savedAt);
  const when: string = `${saved.getMonth() + 1}/${saved.getDate()} ${String(saved.getHours()).padStart(2, '0')}:${String(saved.getMinutes()).padStart(2, '0')}`;
  return (
    <ResumeRoot role="region" aria-label={`임시 저장한 ${entityLabel}`}>
      <strong>작성하던 {entityLabel}가 있어요</strong>
      <span>{title ? `"${title}" · ` : ''}{when} 저장</span>
      <div>
        <button type="button" onClick={onResume} style={{ border: 'none', background: FORM_COLOR.primary, color: '#fff' }}>이어서 쓰기</button>
        <button type="button" onClick={onFresh} style={{ border: `1px solid ${FORM_COLOR.border}`, background: '#fff', color: FORM_COLOR.sub }}>새로 쓰기</button>
      </div>
    </ResumeRoot>
  );
};
